import { flushSync } from 'react-dom';

// SMOOTH LAYOUT CHANGES (stats mode: a profile opens / closes / moves to another line).
//
// The layout changes ONCE (React commits the new state), and then everything that moved is
// slid from where it WAS to where it IS with a transform — the browser's compositor does that
// without re-laying-out the page on every frame. (Animating the height of the gap instead made
// the whole page re-flow 60 times a second: that was the stutter.)
//
//   flip(container, commit, { ghost })
//     container : the block whose inside is changing (marked pieces: [data-flip]) — whatever
//                 comes after it on the page is followed too
//     commit    : () => { setState(…) }   the change itself
//     ghost     : an element that is about to disappear (the old profile): a copy of it stays
//                 on screen and is wiped away while the rest slides over its place
//   reveal(el)  : a new block (the new profile) is uncovered from its top edge down, in step
//                 with the pieces sliding out of its way

export const FLIP_MS = 300;
export const FLIP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// everything that comes after `el` on the page, as whole blocks (the next siblings of el and of each of its parents)
function after(el, root) {
  const out = [];
  for (let n = el; n && n !== root && n.parentElement; n = n.parentElement) {
    for (let s = n.nextElementSibling; s; s = s.nextElementSibling) out.push(s);
  }
  return out;
}

// local px for 1 screen px (the pool page can be shown with CSS zoom on small laptops)
const scaleOf = (el) => {
  const h = el.offsetHeight;
  if (!h) return 1;
  const r = el.getBoundingClientRect().height / h;
  return r > 0.2 && r < 5 ? r : 1;
};

//     settle    : () => {…}  runs right after the change, before anything is measured — e.g. scroll a list so the
//                 chosen row sits at its spot: the scroll becomes part of the SAME slide (one motion, not two)
export function flip(container, commit, { ghost = null, settle = null } = {}) {
  if (!container || reduced() || typeof container.animate !== 'function') { flushSync(commit); settle?.(); return; }
  const root = container.closest('.pool-lobby') || document.body;
  const pieces = [...container.querySelectorAll('[data-flip]'), ...after(container, root)];
  const before = new Map(pieces.map((e) => [e, e.getBoundingClientRect().top]));

  // the block that is leaving: a copy stays where it was, out of the layout, and is wiped from the bottom up
  let copy = null;
  if (ghost && ghost.offsetParent) {
    copy = ghost.cloneNode(true);
    Object.assign(copy.style, {
      position: 'absolute', top: `${ghost.offsetTop}px`, left: `${ghost.offsetLeft}px`, width: `${ghost.offsetWidth}px`,
      margin: '0', pointerEvents: 'none', zIndex: '0',
    });
    copy.removeAttribute('data-flip');
    copy.setAttribute('aria-hidden', 'true');
    copy.classList.add('pp-ghost');
    ghost.offsetParent.appendChild(copy);
  }

  flushSync(commit);   // the one and only layout change
  settle?.();          // + the list's scroll position, if it has to move

  pieces.forEach((e) => {
    if (!e.isConnected) return;
    const d = (before.get(e) - e.getBoundingClientRect().top) / scaleOf(e);
    if (Math.abs(d) < 0.5) return;
    e.animate([{ transform: `translateY(${d}px)` }, { transform: 'translateY(0)' }],
      { duration: FLIP_MS, easing: FLIP_EASE, composite: 'add' });
  });
  if (copy) {
    const a = copy.animate([{ clipPath: 'inset(0 0 0 0)', opacity: 1 }, { clipPath: 'inset(0 0 100% 0)', opacity: 0.2 }],
      { duration: FLIP_MS, easing: FLIP_EASE, fill: 'forwards' });
    const done = () => copy.remove();
    a.onfinish = done; a.oncancel = done;
    setTimeout(done, FLIP_MS + 200);
  }
}

// a block that has just appeared: uncovered from its top edge down
export function reveal(el) {
  if (!el || reduced() || typeof el.animate !== 'function') return;
  el.animate([{ clipPath: 'inset(0 0 100% 0)', opacity: 0.2 }, { clipPath: 'inset(0 0 0 0)', opacity: 1 }],
    { duration: FLIP_MS, easing: FLIP_EASE });
}

// In a scrolling list: put `row` at its spot — just under the top of the list, with most of the row before it
// still showing (so you see where you are). Called as flip()'s `settle`: the jump is instant in the layout and
// the slide makes it smooth. Does nothing when the row isn't inside a scrolling list.
export function snapRow(row) {
  if (!row) return;
  let box = row.parentElement;
  while (box && box !== document.body) {
    const oy = getComputedStyle(box).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && box.scrollHeight > box.clientHeight + 1) break;
    box = box.parentElement;
  }
  if (!box || box === document.body) return;
  const z = (() => { const h = box.offsetHeight; const r = h ? box.getBoundingClientRect().height / h : 1; return r > 0.2 && r < 5 ? r : 1; })();
  const prev = row.previousElementSibling;
  const peek = prev ? Math.min(prev.getBoundingClientRect().height * 0.8, 64 * z) : 0;
  // where the row IS in the layout — not where a slide still in progress is showing it (quick taps in a row)
  const sliding = (() => { const t = getComputedStyle(row).transform; return t && t !== 'none' ? new DOMMatrixReadOnly(t).m42 * z : 0; })();
  const delta = (row.getBoundingClientRect().top - sliding - box.getBoundingClientRect().top - peek) / z;
  if (Math.abs(delta) > 1) box.scrollTop += delta;
}
